import React, { MutableRefObject, useContext, useEffect, useState } from 'react';
import { Message } from 'talkplus-sdk/dist/model/types';
import Icon from '@common/assets';
import { ChatContext, ChatContextType } from '@components/chat/ChatContext';
import useScroll from '@components/chat/hooks/useScroll';
import Colors from '@domain/constant/colors';
import { AdminMessage } from '@domain/constant/AdminMessageType';
import classnames from 'classnames/bind';
import styles from './index.module.scss';

const cx = classnames.bind(styles);

interface ReplyDetailProps {
  parentMessage: Message | null;
  handleCloseReply: () => void;
}

const ReplyDetail = ({ parentMessage, handleCloseReply }: ReplyDetailProps) => {
  const isReplying = !!parentMessage;

  const getUserName = () => {
    if (parentMessage?.type === 'admin') return '📢JOBDA';
    return parentMessage?.username || '유저';
  };

  const getMessage = () => {
    if (parentMessage?.type === 'admin') {
      switch (parentMessage?.data?.type) {
        case AdminMessage.REMIND_JOB_POSTING_DEADLINE_D_DAY:
          return '[D-Day] 입사지원 마감 안내 ⏰';
        case AdminMessage.REMIND_JOB_POSTING_DEADLINE_D_3:
          return '[D-3] 입사지원 마감 안내 ⏰';
        case AdminMessage.REMIND_JOB_POSTING_DEADLINE:
          return '[D-3] 입사지원 마감 안내 ⏰';
        case AdminMessage.REMIND_JOB_POSTING_REGISTERED:
          return '[New] 공고 등록 안내 🔔';
      }
    }
    return parentMessage?.text || '';
  };

  if (!isReplying) return null;
  return (
    <div className={cx('answerArea')}>
      <div className={cx('textArea')}>
        <span className={cx('name')}>{getUserName()} :</span>
        <span className={cx('message')} title={getMessage()}>
          {getMessage()}
        </span>
      </div>
      <button className={cx('closeBtn')} onClick={handleCloseReply}>
        <Icon name={'closeLight'} width={24} height={24} fill={Colors.C_COOL_GRAY_70} />
      </button>
    </div>
  );
};

interface ChatInputProps {
  useScrollProps: ReturnType<typeof useScroll>;
  textAreaRef: MutableRefObject<HTMLTextAreaElement | null>;
  parentMessage: Message | null;
  handleCloseReply: () => void;
}

const ChatInput = ({
  useScrollProps: { toScrollBottom },
  textAreaRef,
  parentMessage,
  handleCloseReply,
}: ChatInputProps) => {
  const { userData, channel, sendMessage } = useContext<ChatContextType>(ChatContext);

  const [text, setText] = useState<string>('');

  const isReplying = !!parentMessage;
  const placeholder = isReplying
    ? '↳ 답장'
    : `${userData?.user?.username}님, \n다른 지원자들과 함께 채용 정보를 나누어 보세요.`;
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!channel) return;
      sendMessage({
        channelId: channel.id,
        text,
        parentMessageId: isReplying ? parentMessage?.id : undefined,
      });
      isReplying && handleCloseReply();
      setText('');
      toScrollBottom();
    }
  };

  useEffect(() => {
    const el = textAreaRef?.current;
    if (!el) return;
    el.focus();
  }, []);

  return (
    <div className={cx('inputArea')}>
      <div className={cx('inputWrap')}>
        <textarea
          ref={textAreaRef}
          className={cx('textarea')}
          rows={2}
          value={text}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          spellCheck={false}
          placeholder={placeholder}
          maxLength={1000}
        />
      </div>
      <ReplyDetail parentMessage={parentMessage} handleCloseReply={handleCloseReply} />
    </div>
  );
};

export default ChatInput;
